import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import NotificationPermissionModal from "./components/NotificationPermissionModal";
import { registerServiceWorker, updateBadge } from "./services/pushNotifications";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RoleSelectionPage from "./pages/RoleSelectionPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import JobsPage from "./pages/JobsPage";
import JobDetailPage from "./pages/JobDetailPage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import CandidatesPage from "./pages/CandidatesPage";
import CandidateDetailPage from "./pages/CandidateDetailPage";
import CandidateDashboard from "./pages/CandidateDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import ProviderDashboard from "./pages/ProviderDashboard";
import SubscriptionPage from "./pages/SubscriptionPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
// Legal & Info Pages
import ImpressumPage from "./pages/ImpressumPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import ContactPage from "./pages/ContactPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import ReportAbusePage from "./pages/ReportAbusePage";
import MessagesPage from "./pages/MessagesPage";
// Guides
import GuidesPage from "./pages/GuidesPage";
import PermessiLavoroGuide from "./pages/guides/PermessiLavoroGuide";
import CVSvizzeroGuide from "./pages/guides/CVSvizzeroGuide";
import StipendioNettoGuide from "./pages/guides/StipendioNettoGuide";
import "@/App.css";

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Se l'utente è "pending", reindirizza alla selezione ruolo
  if (user.user_type === "pending") {
    return <Navigate to="/select-role" replace />;
  }
  
  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  // Register Service Worker on app load
  useEffect(() => {
    registerServiceWorker();
  }, []);
  
  // Update badge when user changes or notifications update
  useEffect(() => {
    if (user) {
      // Fetch unread count and update badge
      const fetchUnreadCount = async () => {
        try {
          const token = localStorage.getItem('token');
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${API_URL}/api/notifications/unread-count`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            updateBadge(data.count || 0);
          }
        } catch (e) {
          // Silent fail - badge is optional
        }
      };
      fetchUnreadCount();
    } else {
      updateBadge(0);
    }
  }, [user]);
  
  const getDashboardRedirect = () => {
    if (!user) return "/login";
    if (user.user_type === "pending") return "/select-role";
    switch (user.user_type) {
      case "candidate": return "/dashboard/candidate";
      case "company": return "/dashboard/company";
      case "service_provider": return "/dashboard/provider";
      case "admin": return "/admin";
      default: return "/";
    }
  };
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardRedirect()} replace /> : <LoginPage />} />
      <Route path="/register" element={user && user.user_type !== "pending" ? <Navigate to={getDashboardRedirect()} replace /> : <RegisterPage />} />
      <Route path="/select-role" element={user?.user_type === "pending" ? <RoleSelectionPage /> : <Navigate to={getDashboardRedirect()} replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Annunci lavoro - Job Listings */}
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/jobs/:jobId" element={<JobDetailPage />} />
      
      {/* Offro servizi - Service Listings */}
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
      
      {/* Candidati - Candidate Profiles */}
      <Route path="/candidates" element={<CandidatesPage />} />
      <Route path="/candidates/:candidateId" element={<CandidateDetailPage />} />
      
      {/* Messages - Internal Messaging */}
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute allowedTypes={["candidate", "company", "service_provider"]}>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Legal & Info Pages */}
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/help" element={<HowItWorksPage />} />
      <Route path="/report" element={<ReportAbusePage />} />
      
      {/* Dashboards */}
      <Route 
        path="/dashboard/candidate" 
        element={
          <ProtectedRoute allowedTypes={["candidate"]}>
            <CandidateDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/company" 
        element={
          <ProtectedRoute allowedTypes={["company"]}>
            <CompanyDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/provider" 
        element={
          <ProtectedRoute allowedTypes={["service_provider"]}>
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Subscription & Payment */}
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute allowedTypes={["candidate", "service_provider"]}>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute allowedTypes={["candidate", "service_provider"]}>
            <PaymentSuccessPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Guide e Risorse */}
      <Route path="/guide-lavoro" element={<GuidesPage />} />
      <Route path="/guide/permessi-lavoro" element={<PermessiLavoroGuide />} />
      <Route path="/guide/cv-svizzero" element={<CVSvizzeroGuide />} />
      <Route path="/guide/stipendio-netto" element={<StipendioNettoGuide />} />
      
      {/* Legacy redirect */}
      <Route path="/dashboard/worker" element={<Navigate to="/dashboard/candidate" replace />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  const { user } = useAuth();
  
  return (
    <>
      <AppRoutes />
      <Toaster position="top-right" richColors />
      {user && <NotificationPermissionModal user={user} />}
    </>
  );
}

export default App;
